















import { defineComponent, PropType, computed } from '@vue/composition-api';

/**
 * Reusable button component. Default color is secondary, default size is small.
 * The button can contain only text, icon(s) and text or only an icon. In the latter case,
 * it has to be determined if the icon is displayed as a normal or a round button.
 * In case a href is given, an `a` tag instead of a `button` tag is rendered.
 */
export default defineComponent({
	props: {
		/**
		 * Show an outlined button instead of a filled one.
		 */
		outlined: Boolean,
		/**
		 * Disable the button. (Necessary to be able to disable a link as well)
		 */
		disabled: Boolean,
		/**
		 * Apply a different style, in case the button should look like a link, but still act as a button
		 */
		linkStyle: Boolean,
		/**
		 * Render an `a` tag instead of a `button` tag in case a href is given
		 */
		href: String,
		/**
		 * target for `a` tag
		 */
		target: String,
		/**
		 * Vary the size of the button: small, medium or large. Button without an explicit size given is small.
		 */
		transparent: Boolean,

		size: {
			type: String as PropType<'medium' | 'large'>,
		},
		/**
		 * Sets the type of the button in case it's only an icon: either an icon-only button or a round icon-only button (fab).
		 * Values: icon, icon-fab. If not given, a primary button is displayed.
		 */
		iconType: {
			type: String as PropType<'icon' | 'icon-fab'>,
		},
		/**
		 * Sets color of button to primary color
		 */
		primary: {
			type: Boolean,
			default: false,
		},
	},
	setup(props, { emit }) {
		const classes = computed(() => [
			'olb-button',
			props.outlined && 'olb-button-outlined',
			props.size && `olb-button-${props.size}`,
			props.disabled && 'olb-disabled',
			props.iconType && `olb-button-${props.iconType}`,
			props.linkStyle && 'olb-button-as-link',
			props.primary && 'olb-button-primary',
			props.transparent && 'olb-button-transparent',
		]);

		function clickHandler(event: Event): void {
			emit('click', event);
		}

		return { classes, clickHandler };
	},
});
